import React, { useMemo, useCallback } from 'react';
import { UISchema, UIComponentProps } from '@types/index';
import { componentRegistry, schemaValidator, expressionEvaluator, EvaluationContext } from './registry';

interface SchemaRendererProps {
  schema: UISchema;
  context?: EvaluationContext;
  onAction?: (actionId: string, payload?: any) => void;
}

export const SchemaRenderer: React.FC<SchemaRendererProps> = ({
  schema,
  context = {},
  onAction,
}) => {
  const validation = useMemo(() => schemaValidator.validate(schema), [schema]);

  if (!validation.valid) {
    return (
      <div style={{ color: 'red', padding: '10px' }}>
        <strong>Schema Validation Error:</strong>
        <ul>
          {validation.errors.map((error, idx) => (
            <li key={idx}>{error}</li>
          ))}
        </ul>
      </div>
    );
  }

  return <SchemaRendererInternal schema={schema} context={context} onAction={onAction} />;
};

interface SchemaRendererInternalProps extends SchemaRendererProps {}

const SchemaRendererInternal: React.FC<SchemaRendererInternalProps> = ({
  schema,
  context,
  onAction,
}) => {
  // Check condition
  if (schema.condition && !expressionEvaluator.evaluateCondition(schema.condition, context)) {
    return null;
  }

  const componentType = schema.type.toLowerCase();
  const Component = componentRegistry.get(componentType);

  if (!Component) {
    return (
      <div style={{ color: 'orange', padding: '10px' }}>
        Warning: Component type "{schema.type}" is not registered
      </div>
    );
  }

  const props: UIComponentProps = {
    ...schema.props,
    id: schema.id,
  };

  // Add event handlers
  if (schema.events && onAction) {
    Object.entries(schema.events).forEach(([eventName, actionId]) => {
      const eventKey = `on${eventName.charAt(0).toUpperCase()}${eventName.slice(1)}`;
      props[eventKey] = () => onAction(actionId, { from: schema.id });
    });
  }

  // Render children if present
  let children: React.ReactNode;
  if (schema.children && schema.children.length > 0) {
    children = schema.children.map((childSchema, idx) => (
      <SchemaRendererInternal
        key={childSchema.id || idx}
        schema={childSchema}
        context={context}
        onAction={onAction}
      />
    ));
  }

  return <Component {...props}>{children}</Component>;
};

export default SchemaRenderer;
